import { portalApi } from '../../../api';
import CompanySettingTypes from '../../../constants/companySettings';
import { arrayFilterAndMap, createMap, sortAlphaNumeric, unique } from '../../../utils/arrayUtil';
import parseInsured from '../../parsers/insured';
import { createErrorToast, createSuccessToast, createToast } from '../app';
import { selectCompanyId, selectCompanySettings, selectCompanySettingValue } from '../company';

import {
  defaultEmailBody,
  defaultEmailSignature,
  defaultEmailTemplateTypes,
  insuranceRequestDefaultTemplate
} from './defaults';

const DEFAULT_STATE = {
  insureds: {
    data: [],
    pageInfo: null
  },
  companyEmailTemplates: { data: [] },
  companySignatureTemplates: { data: [] },
  departmentEmailTemplates: { data: [] },
  departmentSignatureTemplates: { data: [] },
  selectedDepartment: {},
  companyCodes: [],
  checkboxCodes: {},
  currentSignature: null,
  currentSignatureTemplateName: null,
  currentBodyTemplateName: null,
  currentMessageHtml: null,
  currentSubjectHtml: null,
  defaultSignature: null,
  defaultTemplate: null,
  isSending: false,
  isSent: false,
  previewModalLoading: false,
  previewModal: {},
  searchParams: null,
  recipients: {},
  successMessage: '',
  errorMessage: '',
  insuranceRequest: {
    to: null,
    cc: null
  }
};

/* Actions
================================================== */
const EMAILS_SET_SEARCH_PARAMS = 'EMAILS_SET_SEARCH_PARAMS';
const EMAILS_GET_QUERY_RECIPIENTS_PAGE = 'EMAILS_GET_QUERY_RECIPIENTS_PAGE';
const EMAILS_GET_QUERY_RECIPIENTS_PAGE_SUCCESS = 'EMAILS_GET_QUERY_RECIPIENTS_PAGE_SUCCESS';
const EMAILS_GET_QUERY_RECIPIENTS_PAGE_FAILURE = 'EMAILS_GET_QUERY_RECIPIENTS_PAGE_FAILURE';

export const getSearchQueryRecipientsPage = (
  passedSearchParams = null,
  passedPageData = { page: 1, pageSize: 100 }
) => async (dispatch, getState) => {
  dispatch({ type: EMAILS_GET_QUERY_RECIPIENTS_PAGE });

  if (passedSearchParams) {
    dispatch({
      camelCase: false,
      type: EMAILS_SET_SEARCH_PARAMS,
      searchParams: passedSearchParams
    });
  }

  const useAggregates = selectCompanySettingValue(CompanySettingTypes.ReportsHideNonPolicyHolders)(getState());
  const previousPageData = selectEmailRecipientInsureds(getState()).pageInfo;
  const searchParams = selectSearchParams(getState()) ?? passedSearchParams;
  const companyId = selectCompanyId(getState());
  const companySettings = selectCompanySettings(getState());

  const pageData = previousPageData
    ? { page: previousPageData.currentPage + 1, pageSize: previousPageData.size }
    : passedPageData;

  if (previousPageData && previousPageData?.lastIndex) {
    pageData.after = {
      companyName: previousPageData.lastIndex.companyName,
      insured_id: previousPageData.lastIndex.insuredId
    };
  }

  try {
    const companyUsers = await portalApi.getCompanyUsers(companyId);

    const { data, ...pageInfo } = await portalApi.searchInsuranceRequirements({
      ...searchParams,
      aggregates: useAggregates && {
        primary_source_key: 'insured.id',
        primary_source_name: 'insured_id',
        nested_size: 1
      }
    }, pageData);

    dispatch({
      type: EMAILS_GET_QUERY_RECIPIENTS_PAGE_SUCCESS,
      data,
      pageInfo,
      users: companyUsers,
      companySettings
    });
  } catch (error) {
    dispatch({
      type: EMAILS_GET_QUERY_RECIPIENTS_PAGE_FAILURE,
      error
    });
  }
};

const UPDATE_INSURED_RECIPIENTS = 'UPDATE_INSURED_RECIPIENTS';
export const updateInsuredRecipients = (insuredId, recipients, jobId = null, recordId = null) => dispatch => {
  dispatch({ type: UPDATE_INSURED_RECIPIENTS, insuredId, jobId, recordId, recipients, camelCase: false });
};

const EMAILS_SEND_INSURED_BULK_EMAIL = 'EMAILS_SEND_INSURED_BULK_EMAIL';
const EMAILS_SEND_INSURED_BULK_EMAIL_SUCCESS = 'EMAILS_SEND_INSURED_BULK_EMAIL_SUCCESS';
const EMAILS_SEND_INSURED_BULK_EMAIL_FAILURE = 'EMAILS_SEND_INSURED_BULK_EMAIL_FAILURE';

export const sendInsuredBulkEmail = () => async (dispatch, getState) => {
  dispatch({ type: EMAILS_SEND_INSURED_BULK_EMAIL });
  const checkboxCodes = selectCheckboxMap(getState());
  const codeLetters = arrayFilterAndMap(Object.values(checkboxCodes), code => !!code.checked, code => code.letter);
  const searchParams = selectSearchParams(getState());
  const recipientChanges = selectRecipients(getState());
  const body = selectCurrentMessageHtml(getState());
  const signature = selectCurrentSignature(getState());
  const subject = selectCurrentSubjectHtml(getState());
  const department = selectCurrentDepartment(getState());
  const companyId = selectCompanyId(getState());
  const { total: expectedNumberOfEmails } = selectEmailSearchPageInfo(getState());

  const data = {
    search_params: searchParams,
    recipient_modifications: recipientChanges,
    email_body: `${body}<br />${signature}`,
    email_subject: subject,
    code_letters: codeLetters,
    company_id: companyId,
    expected_number_of_emails: expectedNumberOfEmails
  };

  try {
    const response = await portalApi.sendBulkEmail(data);
    dispatch({ type: EMAILS_SEND_INSURED_BULK_EMAIL_SUCCESS, response });
    const redirectUrl = department?.departmentId ? `reports?department_id=${department.departmentId}` : 'reports';

    setTimeout(() => window.location.assign(
      window.location.href.replace(/draft-email.*/, redirectUrl)
    ), 2000);
  } catch (error) {
    dispatch({ type: EMAILS_SEND_INSURED_BULK_EMAIL_FAILURE, error });
  }
};

const EMAILS_GET_ALL_TEMPLATES = 'EMAILS_GET_ALL_TEMPLATES';
const EMAILS_GET_ALL_TEMPLATES_SUCCESS = 'EMAILS_GET_ALL_TEMPLATES_SUCCESS';
const EMAILS_GET_ALL_TEMPLATES_FAILURE = 'EMAILS_GET_ALL_TEMPLATES_FAILURE';

export const getAllCompanyEmailTemplates = companyId => async dispatch => {
  dispatch({ type: EMAILS_GET_ALL_TEMPLATES, companyId });

  const pageOptions = { pageNumber: 1, pageSize: 25 };
  const emailTemplatesFilter = { template_type: 'main_content' };
  const emailSignaturesFilter = { template_type: 'signature' };

  try {
    const companyEmailTemplatePromise = portalApi.getEmailTemplates(companyId, emailTemplatesFilter, pageOptions);
    const companySignatureTemplatePromise = portalApi.getEmailTemplates(companyId, emailSignaturesFilter, pageOptions);

    Promise.all([companyEmailTemplatePromise, companySignatureTemplatePromise])
      .then(res => {
        const { data: emailTemplateData, ...emailTemplatePageInfo } = res[0];
        const { data: emailSignatureData, ...emailSignaturePageInfo } = res[1];
        dispatch({
          type: EMAILS_GET_ALL_TEMPLATES_SUCCESS,
          emailTemplateData,
          emailTemplatePageInfo,
          emailSignatureData,
          emailSignaturePageInfo
        });
      });
  } catch (error) {
    dispatch({ type: EMAILS_GET_ALL_TEMPLATES_FAILURE, error });
  }
};

const EMAILS_GET_COMPANY_EMAIL_TEMPLATES = 'EMAILS_GET_COMPANY_EMAIL_TEMPLATES';
const EMAILS_GET_COMPANY_EMAIL_TEMPLATES_SUCCESS = 'EMAILS_GET_COMPANY_EMAIL_TEMPLATES_SUCCESS';
const EMAILS_GET_COMPANY_EMAIL_TEMPLATES_FAILURE = 'EMAILS_GET_COMPANY_EMAIL_TEMPLATES_FAILURE';

export const getCompanyEmailTemplates = (companyId, pageOptions = {
  pageSize: 25,
  pageNumber: 1
}) => async dispatch => {
  dispatch({ type: EMAILS_GET_COMPANY_EMAIL_TEMPLATES });

  const filter = { template_type: 'main_content' };

  try {
    const { data, ...pageInfo } = await portalApi.getEmailTemplates(companyId, filter, pageOptions);
    dispatch({
      type: EMAILS_GET_COMPANY_EMAIL_TEMPLATES_SUCCESS,
      companyEmailTemplates: { data, pageInfo },
      templates: data,
      aggregate: pageOptions.pageNumber !== 1
    });
  } catch (error) {
    dispatch({ type: EMAILS_GET_COMPANY_EMAIL_TEMPLATES_FAILURE, error });
  }
};

const EMAILS_GET_COMPANY_SIGNATURE_TEMPLATES = 'EMAILS_GET_COMPANY_SIGNATURE_TEMPLATES';
const EMAILS_GET_COMPANY_SIGNATURE_TEMPLATES_SUCCESS = 'EMAILS_GET_COMPANY_SIGNATURE_TEMPLATES_SUCCESS';
const EMAILS_GET_COMPANY_SIGNATURE_TEMPLATES_FAILURE = 'EMAILS_GET_COMPANY_SIGNATURE_TEMPLATES_FAILURE';

export const getCompanySignatureTemplates = (companyId, pageOptions = {
  pageSize: 25,
  pageNumber: 1
}) => async dispatch => {
  dispatch({ type: EMAILS_GET_COMPANY_SIGNATURE_TEMPLATES });

  const filter = { template_type: 'signature' };

  try {
    const { data, ...pageInfo } = await portalApi.getEmailTemplates(companyId, filter, pageOptions);
    dispatch({
      type: EMAILS_GET_COMPANY_SIGNATURE_TEMPLATES_SUCCESS,
      companySignatureTemplates: { data, pageInfo },
      aggregate: pageOptions.pageNumber !== 1
    });
  } catch (error) {
    dispatch({ type: EMAILS_GET_COMPANY_SIGNATURE_TEMPLATES_FAILURE, error });
  }
};

const EMAILS_GET_DEPARTMENT_EMAIL_TEMPLATES = 'EMAILS_GET_DEPARTMENT_EMAIL_TEMPLATES';
const EMAILS_GET_DEPARTMENT_EMAIL_TEMPLATES_SUCCESS = 'EMAILS_GET_DEPARTMENT_EMAIL_TEMPLATES_SUCCESS';
const EMAILS_GET_DEPARTMENT_EMAIL_TEMPLATES_FAILURE = 'EMAILS_GET_DEPARTMENT_EMAIL_TEMPLATES_FAILURE';

export const getDepartmentEmailTemplates = (
  companyId,
  departmentId,
  pageOptions = { pageSize: 25, pageNumber: 1 }
) => async dispatch => {
  dispatch({ type: EMAILS_GET_DEPARTMENT_EMAIL_TEMPLATES });

  const filter = {
    template_type: 'main_content',
    department_id: departmentId
  };

  try {
    const { data, ...pageInfo } = await portalApi.getEmailTemplates(companyId, filter, pageOptions);
    dispatch({
      type: EMAILS_GET_DEPARTMENT_EMAIL_TEMPLATES_SUCCESS,
      departmentEmailTemplates: { data, pageInfo },
      aggregate: pageOptions.pageNumber !== 1
    });
  } catch (error) {
    dispatch({ type: EMAILS_GET_DEPARTMENT_EMAIL_TEMPLATES_FAILURE, error });
  }
};

const EMAILS_GET_DEPARTMENT_SIGNATURE_TEMPLATES = 'EMAILS_GET_DEPARTMENT_SIGNATURE_TEMPLATES';
const EMAILS_GET_DEPARTMENT_SIGNATURE_TEMPLATES_SUCCESS = 'EMAILS_GET_DEPARTMENT_SIGNATURE_TEMPLATES_SUCCESS';
const EMAILS_GET_DEPARTMENT_SIGNATURE_TEMPLATES_FAILURE = 'EMAILS_GET_DEPARTMENT_SIGNATURE_TEMPLATES_FAILURE';

export const getDepartmentSignatureTemplates = (
  companyId,
  departmentId,
  pageOptions = { pageSize: 25, pageNumber: 1 }
) => async dispatch => {
  dispatch({ type: EMAILS_GET_DEPARTMENT_SIGNATURE_TEMPLATES });

  const filter = {
    template_type: 'signature',
    department_id: departmentId
  };

  try {
    const { data, ...pageInfo } = await portalApi.getEmailTemplates(companyId, filter, pageOptions);
    dispatch({
      type: EMAILS_GET_DEPARTMENT_SIGNATURE_TEMPLATES_SUCCESS,
      departmentSignatureTemplates: { data, pageInfo },
      aggregate: pageOptions.pageNumber !== 1
    });
  } catch (error) {
    dispatch({ type: EMAILS_GET_DEPARTMENT_SIGNATURE_TEMPLATES_FAILURE, error });
  }
};

const EMAILS_SET_CURRENT_DEPARTMENT = 'EMAILS_SET_CURRENT_DEPARTMENT';

export const setCurrentDepartment = department => async dispatch => {
  dispatch({ type: EMAILS_SET_CURRENT_DEPARTMENT, department });
};

const EMAILS_SAVE_TEMPLATE = 'EMAILS_SAVE_TEMPLATE';
const EMAILS_SAVE_TEMPLATE_SUCCESS = 'EMAILS_SAVE_TEMPLATE_SUCCESS';
const EMAILS_SAVE_TEMPLATE_FAILURE = 'EMAILS_SAVE_TEMPLATE_FAILURE';

export const saveEmailTemplate = (payload, onSuccess = () => {}) => async (dispatch, getState) => {
  dispatch({ type: EMAILS_SAVE_TEMPLATE, payload });

  if (payload.template_type === 'main_content') {
    const { checkboxCodes } = getState().emails;
    payload.template_info.code_letters = checkboxCodes;
  }

  let newTemplate;

  dispatch(createToast({ title: 'Saving template' }));
  try {
    if (payload.department_id) {
      newTemplate = await portalApi.saveDepartmentEmailTemplate(payload.department_id, payload);
    } else {
      newTemplate = await portalApi.saveCompanyEmailTemplate(payload.company_id, payload);
    }

    dispatch({ type: EMAILS_SAVE_TEMPLATE_SUCCESS, newTemplate });
    dispatch(createSuccessToast({ title: 'Template Saved!' }));
    onSuccess();
  } catch (error) {
    dispatch({ type: EMAILS_SAVE_TEMPLATE_FAILURE, error });
    dispatch(createErrorToast({ title: 'Something went wrong...', error }));
  }
};

const EMAILS_DELETE_TEMPLATE = 'EMAILS_DELETE_TEMPLATE';
const EMAILS_DELETE_TEMPLATE_SUCCESS = 'EMAILS_DELETE_TEMPLATE_SUCCESS';
const EMAILS_DELETE_TEMPLATE_FAILURE = 'EMAILS_DELETE_TEMPLATE_FAILURE';

export const deleteEmailTemplate = (template, type, selectedDepartment = null) => async (dispatch, getState) => {
  const companyId = selectCompanyId(getState());
  const { templateId, templateType } = template;

  dispatch({ type: EMAILS_DELETE_TEMPLATE, template });

  dispatch(createToast({ title: 'Deleting Email Template' }));

  try {
    const response = await portalApi.deleteEmailTemplate(templateId);

    dispatch({ type: EMAILS_DELETE_TEMPLATE_SUCCESS, templateId, response });

    dispatch(createSuccessToast({ title: 'Email Template Deleted' }));

    if (templateType === 'main_content' && type === 'company') {
      dispatch(getCompanyEmailTemplates(companyId));
    }

    if (templateType === 'signature' && type === 'company') {
      dispatch(getCompanySignatureTemplates(companyId));
    }

    if (templateType === 'main_content' && type === 'department' && selectedDepartment) {
      dispatch(getDepartmentEmailTemplates(companyId, selectedDepartment.departmentId));
    }

    if (templateType === 'signature' && type === 'department' && selectedDepartment) {
      dispatch(getDepartmentSignatureTemplates(companyId, selectedDepartment.departmentId));
    }
  } catch (error) {
    dispatch({ type: EMAILS_DELETE_TEMPLATE_FAILURE, error });
    dispatch(createErrorToast({ title: 'There was a problem deleting your email template', error }));
  }
};

const RESET_TO_DEFAULT_CONTENT_TEMPLATE = 'RESET_TO_DEFAULT_CONTENT_TEMPLATE';

export const resetToDefaultContentTemplate = () => dispatch => {
  dispatch({ type: RESET_TO_DEFAULT_CONTENT_TEMPLATE });
};

const RESET_TO_DEFAULT_SIGNATURE_TEMPLATE = 'RESET_TO_DEFAULT_SIGNATURE_TEMPLATE';

export const resetToDefaultSignatureTemplate = () => dispatch => {
  dispatch({ type: RESET_TO_DEFAULT_SIGNATURE_TEMPLATE });
};

const EMAILS_SET_DEFAULT_TEMPLATE = 'EMAILS_SET_DEFAULT_TEMPLATE';
const EMAILS_SET_DEFAULT_TEMPLATE_SUCCESS = 'EMAILS_SET_DEFAULT_TEMPLATE_SUCCESS';
const EMAILS_SET_DEFAULT_TEMPLATE_FAILURE = 'EMAILS_SET_DEFAULT_TEMPLATE_FAILURE';

export const setDefaultTemplates = (companyId, bodyTemplate = null) => async (dispatch, getState) => {
  dispatch({ type: EMAILS_SET_DEFAULT_TEMPLATE, companyId });

  const checkboxMap = selectCheckboxMap(getState());
  let defaultTemplate;
  let defaultSignature;
  const insuranceRequestTemplate = {
    ...insuranceRequestDefaultTemplate,
    templateInfo: {
      ...insuranceRequestDefaultTemplate.templateInfo,
      codeLetters: checkboxMap
    }
  };

  try {
    const { emailTemplate, signatureTemplate } = await portalApi.getDefaultEmailTemplates(companyId);

    switch (bodyTemplate) {
      case defaultEmailTemplateTypes.insuranceRequestDefaultTemplate:
        defaultTemplate = insuranceRequestTemplate;
        break;
      default: {
        if (bodyTemplate && !emailTemplate) {
          defaultTemplate = {
            ...bodyTemplate,
            templateInfo: {
              ...bodyTemplate.templateInfo,
              codeLetters: checkboxMap
            }
          };
        } else {
          defaultTemplate = emailTemplate ?? insuranceRequestTemplate;
        }
      }
    }

    defaultSignature = signatureTemplate ?? defaultEmailSignature;

    dispatch({ type: EMAILS_SET_DEFAULT_TEMPLATE_SUCCESS, defaultTemplate, defaultSignature });
  } catch (error) {
    dispatch({ type: EMAILS_SET_DEFAULT_TEMPLATE_FAILURE, error });
  }
};

const EMAILS_SET_CURRENT_BODY_TEMPLATE_NAME = 'EMAILS_SET_CURRENT_BODY_TEMPLATE_NAME';

export const setCurrentBodyTemplateName = currentBodyTemplateName => dispatch => {
  dispatch({ type: EMAILS_SET_CURRENT_BODY_TEMPLATE_NAME, currentBodyTemplateName });
};

const EMAILS_SET_CURRENT_MESSAGE_HTML = 'EMAILS_SET_CURRENT_MESSAGE_HTML';

export const setCurrentMessageHtml = currentMessageHtml => dispatch => {
  dispatch({ type: EMAILS_SET_CURRENT_MESSAGE_HTML, currentMessageHtml });
};

const EMAILS_SET_CURRENT_SUBJECT_HTML = 'EMAILS_SET_CURRENT_SUBJECT_HTML';

export const setCurrentSubjectHtml = currentSubjectHtml => dispatch => {
  dispatch({ type: EMAILS_SET_CURRENT_SUBJECT_HTML, currentSubjectHtml });
};

const EMAILS_SET_CURRENT_SIGNATURE_TEMPLATE_NAME = 'EMAILS_SET_CURRENT_SIGNATURE_TEMPLATE_NAME';

export const setCurrentSignatureTemplateName = currentSignatureTemplateName => dispatch => {
  dispatch({ type: EMAILS_SET_CURRENT_SIGNATURE_TEMPLATE_NAME, currentSignatureTemplateName });
};

const EMAILS_SET_CURRENT_SIGNATURE = 'EMAILS_SET_CURRENT_SIGNATURE';

export const setCurrentSignature = currentSignature => dispatch => {
  dispatch({ type: EMAILS_SET_CURRENT_SIGNATURE, currentSignature });
};

const EMAILS_SET_INSUREDS = 'EMAILS_SET_INSUREDS';

export const setInsureds = reqInfo => dispatch => {
  dispatch({ type: EMAILS_SET_INSUREDS, reqInfo });
};

const EMAILS_UPDATE_INSURED_CC = 'EMAILS_UPDATE_INSURED_CC';

export const updateInsuredCcs = (jobId, insuredId, emailCc) => dispatch => {
  dispatch({ type: EMAILS_UPDATE_INSURED_CC, jobId, insuredId, emailCc });
};

const EMAILS_UPDATE_INSURED_RECEIVER = 'EMAILS_UPDATE_INSURED_RECEIVER';

export const updateInsuredReceiver = (jobId, insuredId, email) => dispatch => {
  dispatch({ type: EMAILS_UPDATE_INSURED_RECEIVER, jobId, insuredId, email });
};

const EMAILS_UPDATE_BROKER_CC = 'EMAILS_UPDATE_BROKER_CC';

export const updateBrokerCcs = (jobId, insuredId, emailCc) => dispatch => {
  dispatch({ type: EMAILS_UPDATE_BROKER_CC, jobId, insuredId, emailCc });
};

const EMAILS_UPDATE_BROKER_RECEIVER = 'EMAILS_UPDATE_BROKER_RECEIVER';

export const updateBrokerReceiver = (jobId, insuredId, email) => dispatch => {
  dispatch({ type: EMAILS_UPDATE_BROKER_RECEIVER, jobId, insuredId, email });
};

const EMAILS_SEND_EMAIL = 'EMAILS_SEND_EMAIL';
const EMAILS_SEND_EMAIL_SUCCESS = 'EMAILS_SEND_EMAIL_SUCCESS';
const EMAILS_SEND_EMAIL_FAILURE = 'EMAILS_SEND_EMAIL_FAILURE';

export const sendEmail = (data, onSuccess = () => {}, onFail = () => {}) => async (dispatch, getState) => {
  dispatch({ type: EMAILS_SEND_EMAIL, data });
  const { checkboxCodes } = getState().emails;
  data.code_letters = arrayFilterAndMap(Object.values(checkboxCodes), code => !!code.checked, code => code.letter);

  dispatch(createToast({ title: 'Sending email...' }));

  try {
    const response = await portalApi.sendEmail(data);
    dispatch({ type: EMAILS_SEND_EMAIL_SUCCESS, response });

    dispatch(createSuccessToast({ title: 'Email sent!' }));
    onSuccess();
  } catch (error) {
    dispatch({ type: EMAILS_SEND_EMAIL_FAILURE, error });
    dispatch(createErrorToast({ title: 'Email was not sent', error }));
    onFail();
    throw error;
  }
};

const EMAILS_PREVIEW_EMAIL = 'EMAILS_PREVIEW_EMAIL';
const EMAILS_PREVIEW_EMAIL_SUCCESS = 'EMAILS_PREVIEW_EMAIL_SUCCESS';
const EMAILS_PREVIEW_EMAIL_FAILURE = 'EMAILS_PREVIEW_EMAIL_FAILURE';

export const previewEmail = (recordId, insuredId, jobId, agentId) => async (dispatch, getState) => {
  dispatch({ type: EMAILS_PREVIEW_EMAIL, insuredId, jobId });

  const subject = selectCurrentSubjectHtml(getState());
  const body = selectCurrentMessageHtml(getState());
  const signature = selectCurrentSignature(getState());
  const checkboxCodes = selectCheckboxMap(getState());
  const recipients = selectRecordRecipients(recordId)(getState());

  const emailParams = {
    agent_id: agentId,
    email_subject: subject,
    email_body: `${body}<br /><br />${signature}`,
    code_letters: arrayFilterAndMap(
      Object.values(checkboxCodes),
      code => !!code.checked,
      code => code.letter
    )
  };

  try {
    const response = await portalApi.previewEmail(recordId, emailParams);

    const previewModal = {
      email: selectInsuranceRequestToInput(getState()) || null,
      email_cc: selectInsuranceRequestCcInput(getState()) || null,
      subjectHtml: response.subjectHtml,
      bodyHtml: response.bodyHtml,
      recipients: recipients.map(r => r.email)
    };

    dispatch({ type: EMAILS_PREVIEW_EMAIL_SUCCESS, previewModal });
  } catch (error) {
    dispatch({ type: EMAILS_PREVIEW_EMAIL_FAILURE, error });
  }
};

const EMAILS_GET_INSUREDS = 'EMAILS_GET_INSUREDS';
const EMAILS_GET_INSUREDS_SUCCESS = 'EMAILS_GET_INSUREDS_SUCCESS';
const EMAILS_GET_INSUREDS_FAILURE = 'EMAILS_GET_INSUREDS_FAILURE';

export const getEmailRecipientsInsureds = (companyId, selected, pageData = { pageSize: 100 }) => async dispatch => {
  dispatch({ type: EMAILS_GET_INSUREDS });

  const mappedChecked = createMap(Object.values(selected), 'insuredId');

  const insuredIds = Object.keys(mappedChecked);
  const filters = { insured_id: insuredIds };

  try {
    const { data } = await portalApi.getCompanyInsureds(companyId, pageData, filters);

    const dataWithJobId = data.map(insured => (
      { ...insured, jobId: mappedChecked[insured.insured_id].jobId }));

    dispatch({ type: EMAILS_GET_INSUREDS_SUCCESS, dataWithJobId });
  } catch (error) {
    dispatch({ type: EMAILS_GET_INSUREDS_FAILURE, error });
  }
};

const EMAILS_RESET = 'EMAILS_RESET';

export const emailEditorReset = () => async dispatch => {
  dispatch({ type: EMAILS_RESET });
};

const EMAILS_UPDATE_RECIPIENT_TYPE = 'EMAILS_UPDATE_RECIPIENT_TYPE';

export const updateRecipientType = recipientType => dispatch => {
  dispatch({ type: EMAILS_UPDATE_RECIPIENT_TYPE, recipientType });
};

const EMAILS_GET_COMPANY_CODES = 'EMAILS_GET_COMPANY_CODES';
const EMAILS_GET_COMPANY_CODES_SUCCESS = 'EMAILS_GET_COMPANY_CODES_SUCCESS';
const EMAILS_GET_COMPANY_CODES_FAILURE = 'EMAILS_GET_COMPANY_CODES_FAILURE';

export const getCompanyCodes = companyId => async (dispatch, getState) => {
  dispatch({ type: EMAILS_GET_COMPANY_CODES, companyId });
  const stateCheckboxCodes = getState().emails.checkboxCodes;
  const companyCodes = getState().company.codes;

  try {
    let codes;
    if (!companyCodes?.length) {
      codes = await portalApi.getCompanyCodes(companyId);
    } else {
      codes = companyCodes;
    }

    const checkboxList = codes.map(code => (
      {
        id: code.id,
        letter: code.letter,
        checked: true
      }
    ));

    let checkboxMap;
    if (!Object.keys(stateCheckboxCodes).length) {
      checkboxMap = createMap(checkboxList, 'id');
    } else {
      checkboxMap = stateCheckboxCodes;
    }

    dispatch({ type: EMAILS_GET_COMPANY_CODES_SUCCESS, codes, checkboxMap });
  } catch (error) {
    dispatch({ type: EMAILS_GET_COMPANY_CODES_FAILURE, error });
  }
};

const EMAILS_SET_STATE_CODE_CHECKBOXES = 'EMAILS_SET_STATE_CODE_CHECKBOXES';
export const setStateCodeCheckboxes = codes => dispatch => {
  const checkboxList = codes.map(code => (
    {
      id: code.id,
      letter: code.letter,
      checked: true
    }
  ));

  const checkboxMap = createMap(checkboxList, 'id');

  dispatch({ type: EMAILS_SET_STATE_CODE_CHECKBOXES, codes, checkboxMap });
};

const EMAILS_CHECK_CODE_CHECKBOX = 'EMAILS_CHECK_CODE_CHECKBOX';

export const checkCodeCheckbox = (id, value) => dispatch => {
  dispatch({ type: EMAILS_CHECK_CODE_CHECKBOX, id, value });
};

const EMAILS_CHECK_ALL_CODE_CHECKBOXES = 'EMAILS_CHECK_ALL_CODE_CHECKBOXES';

export const checkAllCodeCheckboxes = value => dispatch => {
  dispatch({ type: EMAILS_CHECK_ALL_CODE_CHECKBOXES, value });
};

const EMAILS_SET_SELECTED_CHECKBOXES = 'EMAILS_SET_SELECTED_CHECKBOXES';

export const setSelectedCheckboxCodes = codeMap => dispatch => {
  dispatch({ type: EMAILS_SET_SELECTED_CHECKBOXES, codeMap });
};

const EMAILS_SET_INSURANCE_REQUEST_INPUTS = 'EMAILS_SET_INSURANCE_REQUEST_INPUTS';

export const setInsuranceRequestInputs = values => dispatch => {
  dispatch({ type: EMAILS_SET_INSURANCE_REQUEST_INPUTS, to: values?.to, cc: values?.cc });
};

/* Reducer
================================================== */
const emailReducer = (state = DEFAULT_STATE, action) => {
  switch (action.type) {
    case EMAILS_SET_SEARCH_PARAMS: {
      return {
        ...state,
        searchParams: action.searchParams
      };
    }

    case EMAILS_GET_QUERY_RECIPIENTS_PAGE_SUCCESS: {
      const userIds = action.users.map(user => user.userId);
      const recordAndInsuredSettingOn = action.companySettings.find(setting => setting.name === CompanySettingTypes.IncludeRecordAndInsuredContactInfoInEmailTools)?.value;
      const agentSettingOn = action.companySettings.find(setting => setting.name === CompanySettingTypes.IncludeAgentInfoInEmailTools)?.value;

      const recipients = action.data.reduce((recordRecipients, current) => {
        const { insured, insurancePolicies, job, insuranceRequirement } = current;
        const { id: recordId, contact: recordContact } = insuranceRequirement;
        const recordContactExists = recordContact?.email || recordContact?.cc;

        const recipientArray = [];

        if (recordContact?.email) {
          const splitEmails = recordContact?.email
            .split(',')
            .map(email => ({
              type: 'insured',
              email: email.trim(),
              id: insured.id,
              job_id: job.id,
              record_id: recordId
            }));

          recipientArray.push(...splitEmails);
        }

        if (recordContact?.cc) {
          const splitEmailCcs = recordContact?.cc
            .split(',')
            .map(email => ({
              type: 'insured',
              email: email.trim(),
              id: insured.id,
              job_id: job.id,
              record_id: recordId
            }));

          recipientArray.push(...splitEmailCcs);
        }

        if (insured?.email) {
          if (recordAndInsuredSettingOn) {
            recipientArray.push({
              type: 'insured',
              email: insured.email,
              id: insured.id,
              job_id: job.id,
              record_id: recordId
            });
          } else if (!recordContactExists) {
            recipientArray.push({
              type: 'insured',
              email: insured.email,
              id: insured.id,
              job_id: job.id,
              record_id: recordId
            });
          }
        }

        if (insured?.cc) {
          const splitInsuredEmailCcs = insured?.cc
            .split(',')
            .map(email => ({
              type: 'insured',
              email: email.trim(),
              id: insured.id,
              job_id: job.id,
              record_id: recordId
            }));

          if (recordAndInsuredSettingOn) {
            recipientArray.push(...splitInsuredEmailCcs);
          } else if (!recordContactExists) {
            recipientArray.push(...splitInsuredEmailCcs);
          }
        }

        if (agentSettingOn) {
          const brokerEmails = unique(arrayFilterAndMap(
            insurancePolicies,
            policy => (!!policy.owner
              && policy.owner?.email?.length
              && !['implementation@pinsadvantage.com', 'jumpstart@pinsadvantage.com'].includes(policy.owner?.email)
              && !userIds.includes(policy.owner.id)
            ),
            policy => ({
              type: 'user',
              id: policy.owner.id,
              email: policy.owner.email,
              job_id: job.id,
              record_id: recordId
            })
          ), option => option.email);

          recipientArray.push(...brokerEmails);
        }

        recordRecipients[recordId] = recordRecipients[recordId] || [];

        return { ...recordRecipients, [recordId]: [...recordRecipients[recordId], ...recipientArray] };
      }, {});

      return {
        ...state,
        recipients: { ...state.recipients, ...recipients },
        insureds: {
          data: [...state.insureds.data, ...action.data],
          pageInfo: action.pageInfo
        },
        companyUsers: action.users
      };
    }

    case UPDATE_INSURED_RECIPIENTS: {
      const remainingRecipients = action.recordId
        ? (state.recipients[action.recordId] || []).filter(r => r.record_id !== action.recordId)
        : [];

      return {
        ...state,
        recipients: {
          ...state.recipients,
          [action.recordId]: [...remainingRecipients, ...action.recipients]
        }
      };
    }

    case EMAILS_SEND_INSURED_BULK_EMAIL_SUCCESS:
      return { ...state, isSending: false, isSent: true };

    case EMAILS_SEND_INSURED_BULK_EMAIL_FAILURE:
      return { ...state, error: action.error, isSending: false };

    case EMAILS_SEND_INSURED_BULK_EMAIL:
      return { ...state, isSending: true };

    case EMAILS_GET_ALL_TEMPLATES_SUCCESS: {
      return {
        ...state,
        companyEmailTemplates: {
          data: action.emailTemplateData,
          ...action.emailTemplatePageInfo
        },
        companySignatureTemplates: {
          data: action.emailSignatureData,
          ...action.emailSignaturePageInfo
        }
      };
    }

    case EMAILS_GET_COMPANY_EMAIL_TEMPLATES_SUCCESS: {
      const sortedTemplates = action.templates.sort(sortAlphaNumeric('templateName'));
      const retrievedData = action.aggregate
        ? [...state.companyEmailTemplates.data, ...action.companyEmailTemplates.data]
        : action.companyEmailTemplates.data;

      return {
        ...state,
        templates: sortedTemplates,
        companyEmailTemplates: {
          data: retrievedData,
          ...action.companyEmailTemplates.pageInfo
        }
      };
    }

    case EMAILS_GET_COMPANY_SIGNATURE_TEMPLATES_SUCCESS: {
      const retrievedData = action.aggregate
        ? [...state.companySignatureTemplates.data, ...action.companySignatureTemplates.data]
        : action.companySignatureTemplates.data;

      return {
        ...state,
        companySignatureTemplates: {
          data: retrievedData,
          ...action.companySignatureTemplates.pageInfo
        }
      };
    }

    case EMAILS_GET_DEPARTMENT_EMAIL_TEMPLATES_SUCCESS: {
      const retrievedData = action.aggregate
        ? [...state.departmentEmailTemplates.data, ...action.departmentEmailTemplates.data]
        : action.departmentEmailTemplates.data;

      return {
        ...state,
        departmentEmailTemplates: {
          data: retrievedData,
          ...action.departmentEmailTemplates.pageInfo
        }
      };
    }

    case EMAILS_GET_DEPARTMENT_SIGNATURE_TEMPLATES_SUCCESS: {
      const retrievedData = action.aggregate
        ? [...state.departmentSignatureTemplates.data, ...action.departmentSignatureTemplates.data]
        : action.departmentSignatureTemplates.data;

      return {
        ...state,
        departmentSignatureTemplates: {
          data: retrievedData,
          ...action.departmentSignatureTemplates.pageInfo
        }
      };
    }

    case EMAILS_SET_CURRENT_DEPARTMENT:
      return {
        ...state,
        selectedDepartment: action.department
      };
    case EMAILS_GET_COMPANY_EMAIL_TEMPLATES_FAILURE: {
      return { ...state, error: { error: action.error } };
    }

    case EMAILS_GET_INSUREDS_SUCCESS: {
      return { ...state, insureds: action.dataWithJobId };
    }

    case EMAILS_RESET: {
      return DEFAULT_STATE;
    }

    case EMAILS_SAVE_TEMPLATE_SUCCESS: {
      if (action.newTemplate.templateType === 'main_content') {
        return {
          ...state,
          currentBodyTemplateName: action.newTemplate.templateName,
          currentSubjectHtml: action.newTemplate.templateInfo.subjectHtml,
          currentMessageHtml: action.newTemplate.templateInfo.html
        };
      }

      if (action.newTemplate.templateType === 'signature') {
        return {
          ...state,
          currentSignatureTemplateName: action.newTemplate.templateName,
          currentSignature: action.newTemplate.templateInfo.html
        };
      }

      return { ...state };
    }

    case EMAILS_SAVE_TEMPLATE_FAILURE: {
      return { ...state, error: { error: action.error } };
    }

    case EMAILS_DELETE_TEMPLATE_SUCCESS: {
      return { ...state };
    }

    case EMAILS_DELETE_TEMPLATE_FAILURE: {
      return { ...state, error: { error: action.error } };
    }

    case EMAILS_SET_CURRENT_BODY_TEMPLATE_NAME: {
      return { ...state, currentBodyTemplateName: action.currentBodyTemplateName };
    }

    case EMAILS_SET_CURRENT_MESSAGE_HTML: {
      return { ...state, currentMessageHtml: action.currentMessageHtml };
    }

    case EMAILS_SET_CURRENT_SUBJECT_HTML: {
      return { ...state, currentSubjectHtml: action.currentSubjectHtml };
    }

    case EMAILS_SET_CURRENT_SIGNATURE: {
      return { ...state, currentSignature: action.currentSignature };
    }

    case EMAILS_SET_CURRENT_SIGNATURE_TEMPLATE_NAME: {
      return { ...state, currentSignatureTemplateName: action.currentSignatureTemplateName };
    }

    case EMAILS_SET_DEFAULT_TEMPLATE_SUCCESS:
      return {
        ...state,
        defaultTemplate: action.defaultTemplate,
        defaultSignature: action.defaultSignature,
        currentBodyTemplateName: action.defaultTemplate.templateName,
        currentSubjectHtml: action.defaultTemplate.templateInfo.subjectHtml,
        currentMessageHtml: action.defaultTemplate.templateInfo.html,
        currentSignatureTemplateName: action.defaultSignature.templateName,
        currentSignature: action.defaultSignature.templateInfo.html,
        checkboxCodes: action.defaultTemplate.templateInfo.codeLetters
      };

    case EMAILS_SET_INSUREDS: {
      return { ...state, insureds: Object.values(action.reqInfo).map(parseInsured) };
    }

    case RESET_TO_DEFAULT_CONTENT_TEMPLATE: {
      return {
        ...state,
        currentMessageHtml: state.defaultTemplate?.templateInfo.html ?? defaultEmailBody.templateInfo.html,
        currentSubjectHtml: state.defaultTemplate?.templateInfo.subjectHtml ?? defaultEmailBody.templateInfo.subjectHtml,
        currentBodyTemplateName: state.defaultTemplate?.templateName ?? defaultEmailBody.templateName,
        checkboxCodes: state.defaultTemplate?.templateInfo.codeLetters ?? {}
      };
    }

    case RESET_TO_DEFAULT_SIGNATURE_TEMPLATE: {
      return {
        ...state,
        currentSignature: state.defaultSignature?.templateInfo.html ?? defaultEmailSignature.templateInfo.html,
        currentSignatureTemplateName: state.defaultSignature?.templateName ?? defaultEmailSignature.templateName
      };
    }

    case EMAILS_UPDATE_INSURED_CC: {
      const updatedInsureds = state.insureds.map(insured => {
        if (insured.jobId === action.jobId && insured.insuredId === action.insuredId) {
          return { ...insured, insuredCc: action.emailCc };
        }

        return insured;
      });

      return {
        ...state,
        insureds: updatedInsureds
      };
    }

    case EMAILS_UPDATE_INSURED_RECEIVER: {
      const updatedInsureds = state.insureds.map(insured => {
        if (insured.jobId === action.jobId && insured.insuredId === action.insuredId) {
          return { ...insured, insuredEmail: action.email };
        }

        return insured;
      });

      return {
        ...state,
        insureds: updatedInsureds
      };
    }

    case EMAILS_UPDATE_BROKER_CC: {
      const updatedBrokers = state.brokers.map(insured => {
        if (insured.jobId === action.jobId && insured.insuredId === action.insuredId) {
          return { ...insured, agentCcs: action.emailCc };
        }

        return insured;
      });

      return {
        ...state,
        brokers: updatedBrokers
      };
    }

    case EMAILS_UPDATE_BROKER_RECEIVER: {
      const updatedBrokers = state.brokers.map(insured => {
        if (insured.jobId === action.jobId && insured.insuredId === action.insuredId) {
          return { ...insured, agentEmails: action.email };
        }

        return insured;
      });

      return {
        ...state,
        brokers: updatedBrokers
      };
    }

    case EMAILS_SEND_EMAIL_FAILURE:
      return { ...state, error: action.error };
    case EMAILS_PREVIEW_EMAIL: {
      return { ...state, previewModalLoading: true };
    }

    case EMAILS_PREVIEW_EMAIL_SUCCESS: {
      return { ...state, previewModal: action.previewModal, previewModalLoading: false };
    }

    case EMAILS_PREVIEW_EMAIL_FAILURE: {
      return { ...state, error: action.error, previewModalLoading: false };
    }

    case EMAILS_UPDATE_RECIPIENT_TYPE:
      return { ...state, recipientType: action.recipientType };

    case EMAILS_GET_COMPANY_CODES_SUCCESS: {
      if (Object.keys(state.checkboxCodes).length) {
        return { ...state, companyCodes: action.codes };
      }

      return { ...state, companyCodes: action.codes, checkboxCodes: action.checkboxMap };
    }

    case EMAILS_SET_STATE_CODE_CHECKBOXES: {
      if (!Object.keys(state.checkboxCodes).length) {
        return {
          ...state,
          companyCodes: action.codes,
          checkboxCodes: action.checkboxMap
        };
      }

      return { ...state };
    }

    case EMAILS_GET_COMPANY_CODES_FAILURE:
      return { ...state, error: action.error };

    case EMAILS_CHECK_CODE_CHECKBOX:
      return {
        ...state,
        checkboxCodes: {
          ...state.checkboxCodes,
          [action.id]: {
            ...state.checkboxCodes[action.id],
            checked: action.value
          }
        }
      };

    case EMAILS_CHECK_ALL_CODE_CHECKBOXES: {
      const updatedCheckboxes = Object.values(state.checkboxCodes).map(code => ({ ...code, checked: action.value }));
      const updatedCheckboxMap = createMap(updatedCheckboxes, 'id');
      return { ...state, checkboxCodes: updatedCheckboxMap };
    }

    case EMAILS_SET_SELECTED_CHECKBOXES:
      return { ...state, checkboxCodes: action.codeMap };

    case EMAILS_SET_INSURANCE_REQUEST_INPUTS:
      return {
        ...state,
        insuranceRequest: {
          ...state.insuranceRequest,
          to: action.to,
          cc: action.cc
        }
      };

    default:
      return state;
  }
};

/* Selectors
================================================== */
export const selectCompanyEmailTemplates = state => state.emails.companyEmailTemplates;
export const selectCompanySignatureTemplates = state => state.emails.companySignatureTemplates;
export const selectDepartmentEmailTemplates = state => state.emails.departmentEmailTemplates;
export const selectDepartmentSignatureTemplates = state => state.emails.departmentSignatureTemplates;
export const selectCurrentDepartment = state => state.emails.selectedDepartment;
export const selectCurrentMessageHtml = state => state.emails.currentMessageHtml;
export const selectCurrentSubjectHtml = state => state.emails.currentSubjectHtml;
export const selectCurrentSignature = state => state.emails.currentSignature;
export const selectEmailRecipientInsureds = state => state.emails.insureds;
export const selectDefaultTemplate = state => state.emails.defaultTemplate;
export const selectDefaultSignature = state => state.emails.defaultSignature;
export const selectCurrentSignatureTemplateName = state => state.emails.currentSignatureTemplateName;
export const selectCurrentBodyTemplateName = state => state.emails.currentBodyTemplateName;
export const selectPreviewModal = state => state.emails.previewModal;
export const selectPreviewModalLoader = state => state.emails.previewModalLoading;
export const selectEmailsError = state => state.emails.error;
export const selectCheckboxMap = state => state.emails.checkboxCodes;
export const selectSearchParams = state => state.emails.searchParams;
export const selectRecipients = state => state.emails.recipients;
export const selectRecordRecipients = recordId => state => state.emails.recipients[recordId] || [];
export const selectIsSending = state => state.emails.isSending;
export const selectIsSent = state => state.emails.isSent;
export const selectInsuranceRequestToInput = state => state.emails.insuranceRequest.to;
export const selectInsuranceRequestCcInput = state => state.emails.insuranceRequest.cc;
export const selectCompanyUsersFromEmails = state => state.emails.companyUsers;
export const selectEmailSearchPageInfo = state => state.emails.insureds.pageInfo;

export default emailReducer;
